import type { RequestInfo } from '@ember-data/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

export interface BatchCreateAndSignPayload {
  pslfProfileId: string;
  pslfTemplateId: string;
  primaryForm: {
    formType: 'currentEmployer' | 'priorEmployer';
    pslfGroupId?: string | null;
    isNonPartner: boolean;
    isPreApproved: boolean;
    borrowerFields: Record<string, string | number | boolean>;
    employerFields: Record<string, string | number | boolean>;
  };
  pastPeriods: Array<{
    pslfGroupId?: string;
    unknownEmployerFein?: boolean;
    employerFields: {
      employmentStartDate: string;
      employmentEndDate: string;
      employmentHours: number;
      employerName?: string;
    };
  }>;
}

export interface BatchCreateAndSignResponse {
  primaryFormId: string;
  pastPeriodFormIds: string[];
  allFormIds: string[];
}

export interface VerifyBorrowerAddressPayload {
  address: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    postalCode: string;
    country?: string;
  };
}

export interface VerifyBorrowerAddressResponse {
  status: 'valid' | 'invalid' | 'unverified';
  provider: string;
  suggestedAddress?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
}

export function batchCreateAndSignRequest(payload: BatchCreateAndSignPayload): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'pslf-forms' },
    op: 'query',
    resourcePath: 'api/v2/pslf-forms',
  };
  const baseUrl = buildBaseURL(urlOptions);

  const body = JSON.stringify({
    pslf_profile_id: payload.pslfProfileId,
    pslf_template_id: payload.pslfTemplateId,
    primary_form: {
      pslf_group_id: payload.primaryForm.pslfGroupId,
      is_non_partner: payload.primaryForm.isNonPartner,
      is_pre_approved: payload.primaryForm.isPreApproved,
      borrower_fields: payload.primaryForm.borrowerFields,
      employer_fields: payload.primaryForm.employerFields,
      form_type: payload.primaryForm.formType,
    },
    past_periods: payload.pastPeriods.map((period) => ({
      pslf_group_id: period.pslfGroupId,
      is_pre_approved: false,
      employer_fields: {
        ...period.employerFields,
        ...(period.unknownEmployerFein ? { unknownEmployerFein: true } : {}),
      },
      form_type: 'priorEmployer',
    })),
  });

  const headers = new Headers();
  headers.set('X-Reindex-Mode', 'sync');

  return {
    url: `${baseUrl}/batch-create-and-sign`,
    method: 'POST',
    body,
    headers,
    cacheOptions: {
      [SkipCache]: true,
    },
  };
}

export function verifyBorrowerAddressRequest(payload: VerifyBorrowerAddressPayload): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'postal-addresses' },
    op: 'query',
    resourcePath: 'api/v2/postal-addresses',
  };
  const baseUrl = buildBaseURL(urlOptions);

  const body = JSON.stringify({
    address: {
      address_line1: payload.address.addressLine1,
      address_line2: payload.address.addressLine2,
      city: payload.address.city,
      state: payload.address.state,
      postal_code: payload.address.postalCode,
      country: payload.address.country,
    },
  });

  return {
    url: `${baseUrl}/verify-address`,
    method: 'POST',
    body,
    cacheOptions: {
      [SkipCache]: true,
    },
  };
}
